import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Accordion, Card, Col, Form, Row, Table } from "react-bootstrap";
import IProductCategory, { IProductCategoryCreateOrUpdate, IProductCategoryInitResult } from "models/sql/IProductCategory";
import ProductCategoryService from "services/ProductCategoryService";
import { generateLog } from "utilities/MiscUtilities";
import MarkdownEditor from "components/MarkdownEditor";
import { useBeforeUnload } from "utilities/UIUtilities";
import DetailFooter from "components/DetailFooter";
import FileUploadEntry from "components/FileUploadEntry";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

interface IProductCategoriesDetailProps {
    baseData?: IProductCategoryInitResult;
}

const ProductCategoriesDetail = (props: IProductCategoriesDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const { id }: any = useParams();
    const [productCategory, setProductCategory] = useState<IProductCategory>({
        productCategoryId: -1,
        name: "",
        publish: undefined,
        metaImageUrl: undefined,
        metaDescription: undefined,
        mediaUrl: undefined,
        bodyContent: undefined,
        notes: undefined,
        created: undefined,
        systemDataJson: "{}",
        headerImagePath: undefined,
        description: undefined
    });
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page."
    );

    useEffect(() => {
        (async () => {
            if (id !== undefined) {
                globalContext.setLoading(true);

                const accessToken = await globalContext.getAccessTokenAsync();
                const result = await ProductCategoryService.GetAsync(accessToken, id);

                if (result.record) {
                    setProductCategory(result.record);
                } else {
                    globalContext.showErrorMessage("Unable to load Product Category");
                }

                globalContext.setLoading(false);
            }
        })();
    }, [id]);

    const deleteAsync = async () => {
        if (productCategory && productCategory.productCategoryId && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await ProductCategoryService.DeleteAsync(accessToken, productCategory.productCategoryId);
            globalContext.setLoading(false);
            navigate("/ProductCategories");
        }
    };

    const submitFormAsync = async () => {
        if (productCategory) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: IProductCategoryCreateOrUpdate = {
                productCategoryId: productCategory.productCategoryId,
                name: productCategory.name,
                metaImageUrl: productCategory.metaImageUrl,
                metaDescription: productCategory.metaDescription,
                headerImagePath: productCategory.headerImagePath,
                description: productCategory.description,
                mediaUrl: productCategory.mediaUrl,
                bodyContent: productCategory.bodyContent,
                publish: productCategory.publish,
                notes: productCategory.notes,
                systemDataJson: productCategory.systemDataJson,
                created: productCategory.created
            };

            await ProductCategoryService.CreateOrUpdateAsync(accessToken, data);
            globalContext.setLoading(false);
            setHasFormDataChanged(false);

            if (data.productCategoryId === -1) {
                navigate(-1);
            }
        }
    };

    if (productCategory) {
        return (
            <>
                <Row>
                    <Col>
                        <Card className="mb-2 mt-2">
                            <Card.Body>
                                <Card.Title>
                                    {id !== undefined && <strong>{productCategory.name}</strong>}
                                    {id === undefined && <strong>New Product Category</strong>}
                                </Card.Title>

                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Published</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setProductCategory({ ...productCategory, publish: e.target.value == "true" });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={productCategory.publish ? "true" : "false"}>
                                                    <option key={"empty"} value={""}>-</option>
                                                    {
                                                        ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setProductCategory({ ...productCategory, name: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={productCategory.name}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="align-items-end">
                                    <Col>
                                        <FileUploadEntry
                                            title="Thumbnail Image"
                                            defaultValue={productCategory.metaImageUrl}
                                            onChange={newVal => {
                                                setProductCategory({ ...productCategory, metaImageUrl: newVal });
                                                setHasFormDataChanged(true);
                                            }}
                                        />
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3">
                                            <Form.Label>SEO Description</Form.Label>
                                            <Form.Control
                                                type="text"
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setProductCategory({ ...productCategory, metaDescription: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={productCategory.metaDescription}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Header Description</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setProductCategory({ ...productCategory, description: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={productCategory.description}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="align-items-end">
                                    <Col>
                                        <FileUploadEntry
                                            title="Category Page Header Image"
                                            defaultValue={productCategory.headerImagePath}
                                            onChange={newVal => {
                                                setProductCategory({ ...productCategory, headerImagePath: newVal });
                                                setHasFormDataChanged(true);
                                            }}
                                        />
                                    </Col>
                                </Row>

                                <Row className="align-items-end">
                                    <Col>
                                        <FileUploadEntry
                                            title={<>Hero media <i>(Comma seperated list of image and youtube video urls)</i></>}
                                            allowMultiple={true}
                                            rows={4}
                                            defaultValue={productCategory.mediaUrl}
                                            onChange={newVal => {
                                                setProductCategory({ ...productCategory, mediaUrl: newVal });
                                                setHasFormDataChanged(true);
                                            }}
                                        />
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Body Content</Form.Label>
                                            <MarkdownEditor
                                                globalContextRef={globalContextRef.current}
                                                allowUploads={true}
                                                noun="Content"
                                                height={600}
                                                onChange={newVal => {
                                                    setProductCategory({ ...productCategory, bodyContent: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={productCategory?.bodyContent || ""}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="pb-3">
                                    <Col>
                                        <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                                            <Accordion.Item
                                                eventKey="0">
                                                <Accordion.Header><strong>Products</strong></Accordion.Header>
                                                <Accordion.Body>
                                                    <Table>
                                                        <thead>
                                                            <tr>
                                                                <th>Name</th>
                                                                <th>Description</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {
                                                                productCategory.products?.map((e, index) =>
                                                                    <tr
                                                                        key={index}
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={(event) => {
                                                                            event.preventDefault();
                                                                            event.stopPropagation();
                                                                            window.open(`/DPS/Products/Detail/${e.productId}`)
                                                                        }}>
                                                                        <td>
                                                                            <FontAwesomeIcon icon={faUpRightFromSquare as IconProp} /> {e.name}
                                                                        </td>
                                                                        <td>
                                                                            {e.shortDescription}
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            }
                                                        </tbody>
                                                    </Table>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    </Col>
                                </Row>

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row className="align-items-end">
                                        <Col>
                                            <Card className="mb-2">
                                                <Card.Body>
                                                    <Card.Title>
                                                        <strong>Log</strong><br />
                                                    </Card.Title>
                                                    {generateLog<IProductCategory>(productCategory)}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                }
                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Admin Notes</Form.Label>
                                            <MarkdownEditor
                                                globalContextRef={globalContextRef.current}
                                                allowUploads={false}
                                                noun="Note"
                                                onChange={newVal => {
                                                    setProductCategory({ ...productCategory, notes: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={productCategory.notes || ""}
                                            />
                                        </Form.Group>
                                    </Row>
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }
            </>
        );
    } else {
        return (
            <>
                <h1>There was an issue retrieving the Product Category, please try again.</h1>
            </>
        );
    }
}

export default ProductCategoriesDetail;
